// Clubs feed: mirrors screens/HomeScreen.js fetchClubs (:290-357). First page paints
// fast, remaining pages stream in sequentially (cursor pagination, <= 15 pages,
// committed every 3 pages), generation-guarded so a reload drops the old loop.
import { useCallback, useEffect, useRef, useState } from "react";
import { ClubService } from "../../../../services/clubService";
import { msg } from "./clubUtils";

const PER_PAGE = 20;
const MAX_BACKGROUND_PAGES = 15;
const PAGES_PER_COMMIT = 3;

export default function useClubsFeed() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reqRef = useRef(0);
  const genRef = useRef(0);

  const streamRest = useCallback(async (initialCursor, generation) => {
    let cursor = initialCursor;
    let keepGoing = true;
    let pages = 0;
    let buffered = [];
    const flush = () => {
      if (!buffered.length) return;
      const batch = buffered;
      buffered = [];
      setClubs((prev) => [...prev, ...batch]);
    };
    while (keepGoing && pages < MAX_BACKGROUND_PAGES) {
      const { data, error: e, lastDoc, hasMore } = await ClubService.getClubs({ limit: PER_PAGE, startAfterDoc: cursor });
      if (generation !== genRef.current) return;
      if (e || !data || data.length === 0) break;
      buffered.push(...data);
      cursor = lastDoc;
      keepGoing = !!hasMore;
      pages += 1;
      if (pages % PAGES_PER_COMMIT === 0) flush();
    }
    if (generation === genRef.current) flush();
  }, []);

  const load = useCallback(async () => {
    const request = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const { data, error: e, lastDoc, hasMore } = await ClubService.getClubs({ limit: PER_PAGE });
      if (e) throw e;
      if (request !== reqRef.current) return;
      const generation = ++genRef.current;
      setClubs(data || []);
      if (hasMore && lastDoc) streamRest(lastDoc, generation);
    } catch (e) {
      if (request === reqRef.current) setError(msg(e, "Could not load clubs."));
    } finally {
      if (request === reqRef.current) setLoading(false);
    }
  }, [streamRest]);

  useEffect(() => {
    load();
    return () => { reqRef.current += 1; genRef.current += 1; };
  }, [load]);

  return { clubs, loading, error, reload: load };
}
